import { useCallback, useMemo, useRef } from 'react';
import type { AccountNode } from '../../api/types.js';
import {
  NODE_TYPE_LABEL,
  filterAndOrder,
  isNodeSelected,
  pinIdFor,
  type AccountScope,
  type PinStore,
} from '../../state/accountScope.js';
import { color, fontSize, radius, tint } from '../../theme/tokens.js';

/**
 * The region's account list — the header search's table (FR-075, FR-076, PRD §9.9).
 *
 * Three columns, **Name · Type · Action**, over one flat list ordered MSPs → parents → children.
 * A row applies the chart scope; the Action cell pins it to the top of the list.
 *
 * ## Why a real `<table>` and not the palette's listbox
 *
 * The other mode of this panel is a combobox over a `role="listbox"`, and that pattern cannot
 * carry this one: an `option` may not contain a focusable control, so a pin button inside it is
 * a `nested-interactive` violation and unreachable by keyboard. A table row may contain as many
 * controls as it likes. So the two modes use two structures on purpose — the Type column is also
 * a column here, and a column wants a header.
 *
 * ## The render cap
 *
 * ⚠ **`MAX_ROWS` is not paging and there is deliberately no page 2.** A region's list runs to a
 * few thousand children, and the ordering is the feature — page two of a list whose first page
 * is MSPs is a page of parents with no MSP in sight. The cap renders the head of the ordered
 * list and states how many rows it is not showing, so the answer to "mine is not here" is to
 * type, which is what the search box is for. Pinned rows are exempt: they sort to the front, so
 * the cap can never hide one.
 */

const MAX_ROWS = 200;

export interface AccountListProps {
  nodes: AccountNode[];
  query: string;
  pins: PinStore;
  /** The scope currently on the chart, so the row that set it reads as selected. */
  scope: AccountScope | null;
  onSelect: (node: AccountNode) => void;
  /** Focus moves back up to the input on ArrowUp from the first row. */
  onFocusInput: () => void;
}

export function AccountList({
  nodes,
  query,
  pins,
  scope,
  onSelect,
  onFocusInput,
}: AccountListProps) {
  const rows = useMemo(() => filterAndOrder(nodes, query, pins.pins), [nodes, query, pins.pins]);
  const shown = rows.slice(0, MAX_ROWS);
  const hidden = rows.length - shown.length;

  const bodyRef = useRef<HTMLTableSectionElement>(null);

  /**
   * Roving focus down the Name buttons.
   *
   * The rows are real controls, so this moves DOM focus rather than an
   * `aria-activedescendant` — which is the whole reason the table can hold a pin button at all.
   */
  const onRowKeyDown = useCallback(
    (event: React.KeyboardEvent, index: number) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();

      const next = event.key === 'ArrowDown' ? index + 1 : index - 1;
      if (next < 0) {
        onFocusInput();
        return;
      }

      const buttons = bodyRef.current?.querySelectorAll<HTMLButtonElement>('[data-row-select]');
      buttons?.[next]?.focus();
    },
    [onFocusInput],
  );

  if (rows.length === 0) {
    // An empty state, never a blank panel (FR-051) — the same rule the search results follow.
    return (
      <div style={{ padding: '22px 18px' }}>
        <div style={{ fontWeight: 600, fontSize: fontSize.bodyStrong, color: color.ink }}>
          {query.trim()
            ? `No accounts in this region match “${query.trim()}”`
            : 'No accounts in this region'}
        </div>
        <div style={{ marginTop: 4, fontSize: fontSize.body, color: color.inkMuted }}>
          {query.trim()
            ? 'Try a shorter fragment — a facility is usually named after its parent, so the parent’s name finds all of them.'
            : 'This region has no accounts in the current snapshot.'}
        </div>
      </div>
    );
  }

  return (
    <>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontFamily: 'inherit',
          tableLayout: 'fixed',
        }}
      >
        <caption className="sr-only">
          Accounts in this region: MSPs, then parent clients, then child clients. Selecting a row
          scopes the region chart to it.
        </caption>
        <thead>
          <tr>
            {(
              [
                ['Name', 'auto', 'left'],
                ['Type', '120px', 'left'],
                ['Action', '68px', 'right'],
              ] as const
            ).map(([label, width, align]) => (
              <th
                key={label}
                scope="col"
                style={{
                  width: width === 'auto' ? undefined : width,
                  textAlign: align,
                  padding: '7px 16px',
                  position: 'sticky',
                  top: 0,
                  // The header sits over a scrolling body, so it needs an opaque ground of its
                  // own — a transparent one lets the first rows read through it as it passes.
                  background: color.surfaceSubtle,
                  borderBottom: `1px solid ${color.border}`,
                  fontSize: fontSize.tableHeaderWide,
                  fontWeight: 600,
                  letterSpacing: '.4px',
                  textTransform: 'uppercase',
                  color: color.inkSubtle,
                }}
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody ref={bodyRef}>
          {shown.map((node, index) => {
            const selected = isNodeSelected(node, scope);
            const pinned = pins.isPinned(node);

            return (
              <tr
                key={pinIdFor(node)}
                style={{
                  background: selected ? color.surfaceSelected : 'transparent',
                  borderBottom: `1px solid ${color.borderSubtle}`,
                }}
              >
                <td style={{ padding: 0 }}>
                  <button
                    type="button"
                    data-row-select
                    onClick={() => onSelect(node)}
                    onKeyDown={(event) => onRowKeyDown(event, index)}
                    aria-current={selected ? 'true' : undefined}
                    /*
                     * Spelled out, because the visible row is two lines of bare names and the
                     * level lives in a *sibling cell* — read as-is a screen reader hears
                     * "Trinity Health Medefis", which says neither what Trinity is nor what
                     * Medefis is to it. It also states what activating the row does, since
                     * "scope the chart" is not inferable from an account name.
                     */
                    aria-label={`${node.name}, ${NODE_TYPE_LABEL[node.type].toLowerCase()}${
                      node.parentName ? ` under ${node.parentName}` : ''
                    }. Scope the chart to it.`}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '9px 16px',
                      border: 'none',
                      background: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    <span
                      style={{
                        display: 'block',
                        fontSize: fontSize.navItem,
                        fontWeight: selected ? 700 : 600,
                        color: color.ink,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {node.name}
                    </span>
                    {/*
                      The line above the row — a child's parent, a parent's MSP. It is what makes
                      two facilities with the same city name tellable apart, and it is the same
                      string the search matches on, so a hit on a name that is not visible on the
                      row is never a mystery.
                    */}
                    {node.parentName ? (
                      <span
                        style={{
                          display: 'block',
                          fontSize: fontSize.captionStrong,
                          color: color.inkSubtle,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {node.parentName}
                      </span>
                    ) : null}
                  </button>
                </td>

                <td style={{ padding: '9px 16px', verticalAlign: 'top' }}>
                  <span
                    style={{
                      display: 'inline-block',
                      fontSize: fontSize.label,
                      fontWeight: 600,
                      letterSpacing: '.3px',
                      textTransform: 'uppercase',
                      color: TYPE_INK[node.type],
                      background: tint(TYPE_INK[node.type], 10),
                      border: `1px solid ${tint(TYPE_INK[node.type], 24)}`,
                      borderRadius: radius.sm,
                      padding: '2px 7px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {NODE_TYPE_LABEL[node.type]}
                  </span>
                </td>

                <td style={{ padding: '9px 16px', textAlign: 'right', verticalAlign: 'top' }}>
                  <button
                    type="button"
                    onClick={() => pins.toggle(node)}
                    onKeyDown={(event) => onRowKeyDown(event, index)}
                    aria-pressed={pinned}
                    // The name has to carry the account, not just "Pin": a screen reader moving
                    // through the Action column would otherwise hear "Pin" two hundred times.
                    aria-label={`${pinned ? 'Unpin' : 'Pin'} ${node.name}`}
                    title={pinned ? 'Unpin' : 'Pin to top'}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 26,
                      height: 26,
                      padding: 0,
                      cursor: 'pointer',
                      background: pinned ? color.washTeal : color.surface,
                      border: `1px solid ${pinned ? color.borderMint : color.borderChip}`,
                      borderRadius: radius.md,
                      color: pinned ? color.nursing : color.inkFaint,
                      fontFamily: 'inherit',
                    }}
                  >
                    {/* A pushpin. Filled when pinned, outlined when not — the state has to be
                        legible without colour, since `aria-pressed` is not on screen. */}
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill={pinned ? 'currentColor' : 'none'}
                      stroke="currentColor"
                      strokeWidth="1.9"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M9 4h6l-1 5 4 3v2H6v-2l4-3-1-5Z" />
                      <path d="M12 14v6" />
                    </svg>
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {hidden > 0 ? (
        <div
          role="status"
          style={{
            padding: '9px 16px',
            borderTop: `1px solid ${color.border}`,
            background: color.surfaceSubtle,
            fontSize: fontSize.caption,
            color: color.inkMuted,
          }}
        >
          Showing the first {MAX_ROWS} of {rows.length.toLocaleString('en-US')} accounts. Type to
          narrow the list — a parent’s name finds all of its facilities.
        </div>
      ) : null}
    </>
  );
}

/**
 * One ink per level, and each one is a token.
 *
 * A literal hex here is a lint error (`no-restricted-syntax` over `frontend/src/**`), which is
 * how the four unreadable pairs the first theme sweep left behind were found. `tint()` rather
 * than string concatenation for the wash and the border, for the same reason: `${color.x}14`
 * yields `var(--c-x)14` and fails silently to transparent.
 */
const TYPE_INK: Record<AccountNode['type'], string> = {
  msp: color.allied,
  parent: color.nursing,
  child: color.inkMuted,
};
